import React from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';

import { StackedDialog } from '../../../utilities/ModalStackContext';
import { StateManager } from '../../../utilities/stateManager';
import {
  DEFAULT_CONCIERGE_FEE,
  financeInvoiceTotal,
  getIncentives,
  saveFinanceInvoice,
} from '../../../utilities/financeInvoice';

const USD = (v) =>
  (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const blankIncentive = () => ({ key: Math.random().toString(36).slice(2), memo: '', amount: '' });

// Finance invoices are nearly always just the standard concierge fee, so a new
// invoice opens pre-filled and ready to save with a single click.
const emptyForm = () => ({
  conciergeFee: String(DEFAULT_CONCIERGE_FEE),
  participationFee: '',
  adjustments: '',
  incentives: [],
});

export default function FinanceInvoiceModal({ open, onClose, stockNumber, invoice = null, onSaved }) {
  const isEdit = !!invoice;
  const [form, setForm] = React.useState(emptyForm());
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    if (invoice) {
      setForm({
        conciergeFee: invoice.conciergeFee != null ? String(invoice.conciergeFee) : '',
        participationFee: invoice.participationFee ? String(invoice.participationFee) : '',
        adjustments: invoice.adjustments ? String(invoice.adjustments) : '',
        incentives: getIncentives(invoice).map(inc => ({
          key: inc.id,
          id: inc.id,
          date: inc.date,
          memo: inc.memo || '',
          amount: inc.amount != null ? String(inc.amount) : '',
        })),
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, invoice]);

  const setField = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const updateIncentive = (key, field, value) => setForm(prev => ({
    ...prev,
    incentives: prev.incentives.map(inc => inc.key === key ? { ...inc, [field]: value } : inc),
  }));

  const addIncentiveRow = () => setForm(prev => ({ ...prev, incentives: [...prev.incentives, blankIncentive()] }));

  const removeIncentiveRow = (key) => setForm(prev => ({
    ...prev,
    incentives: prev.incentives.filter(inc => inc.key !== key),
  }));

  // Preview mirrors exactly what the shared helper will compute on save.
  const total = financeInvoiceTotal({
    conciergeFee: form.conciergeFee,
    participationFee: form.participationFee,
    adjustments: form.adjustments,
    incentives: form.incentives,
  });

  const handleSave = async () => {
    const unnamed = form.incentives.filter(inc => parseFloat(inc.amount) && !inc.memo.trim());
    if (unnamed.length) {
      StateManager.setAlertAndOpen('Every incentive needs a memo', 'warning');
      return;
    }
    setSaving(true);
    try {
      const saved = await saveFinanceInvoice({
        id: invoice?.id,
        stockNumber,
        invoice: {
          date: invoice?.date,
          conciergeFee: form.conciergeFee,
          participationFee: form.participationFee,
          adjustments: form.adjustments,
          // Drop rows the user added but left blank.
          incentives: form.incentives.filter(inc => inc.memo.trim() || parseFloat(inc.amount)),
        },
      });
      StateManager.setAlertAndOpen(isEdit ? 'Invoice updated' : 'Invoice created', 'success');
      onSaved?.(saved);
      onClose();
    } catch (err) {
      console.error(err);
      StateManager.setAlertAndOpen('Failed to save the finance invoice', 'error');
    }
    setSaving(false);
  };

  return (
    <StackedDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              {isEdit ? 'Edit Finance Invoice' : 'New Finance Invoice'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{stockNumber}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
          <TextField
            label="Concierge Fee"
            type="number"
            size="small"
            fullWidth
            value={form.conciergeFee}
            onChange={setField('conciergeFee')}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            helperText={`Standard fee is ${USD(DEFAULT_CONCIERGE_FEE)}`}
          />
          <TextField
            label="Bank Participation Fee"
            type="number"
            size="small"
            fullWidth
            value={form.participationFee}
            onChange={setField('participationFee')}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            helperText="Optional"
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="overline" sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
            Lender Incentives
          </Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addIncentiveRow}>Add</Button>
        </Box>

        {form.incentives.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            No incentives on this invoice.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2 }}>
            {form.incentives.map((inc) => (
              <Box key={inc.key} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  label="Memo"
                  size="small"
                  fullWidth
                  value={inc.memo}
                  onChange={(e) => updateIncentive(inc.key, 'memo', e.target.value)}
                  placeholder="e.g. Woodside reserve"
                />
                <TextField
                  label="Amount"
                  type="number"
                  size="small"
                  sx={{ width: 150, flexShrink: 0 }}
                  value={inc.amount}
                  onChange={(e) => updateIncentive(inc.key, 'amount', e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
                <IconButton size="small" onClick={() => removeIncentiveRow(inc.key)} sx={{ mt: 0.5 }}>
                  <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        <TextField
          label="Adjustments"
          type="number"
          size="small"
          fullWidth
          value={form.adjustments}
          onChange={setField('adjustments')}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          helperText="Added to the total — enter a negative number to discount"
        />

        <Box sx={{
          mt: 2.5, p: 2, borderRadius: 1.5, bgcolor: 'grey.100',
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Invoice Total</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{USD(total)}</Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save Invoice' : 'Create Invoice'}
        </Button>
      </DialogActions>
    </StackedDialog>
  );
}
